/**
 * ============================================================================
 * DEPARTMENTS STORE ACTIONS
 * Refactored using generic Apollo helpers and departments-specific logic
 * ============================================================================
 */

import * as ACTION_TYPES from './action-types'
import * as MUTATIONS from './mutation-types'
import * as TABLE_MUTATIONS from '@/store/modules/Table/mutation-types'

// Models
import { createDepartment } from '@models/Department'
import { createDepartmentVacation } from '@models/DepartmentVacation'
import { createPlan } from '@models/Plan'

// Generic Apollo helpers
import {
	executeSimpleQuery,
	executeMutation,
	executeQueryWithLoading
} from '@/store/helpers/apollo'

// Departments-specific helpers
import {
	buildDepartmentsColumns,
	buildDepartmentsSort,
	buildDepartmentsPayload,
	updatePartnershipInList
} from '@/store/helpers/departments'

// GraphQL imports
import CreateDepartmentMutation from './Schema/Mutations/CreateDepartment.gql'
import DeleteDepartmentMutation from './Schema/Mutations/DeleteDepartment.gql'
import EditDepartmentMutation from './Schema/Mutations/EditDepartment.gql'
import CreatePartnershipMutation from './Schema/Mutations/CreatePartnership.gql'
import EditPartnershipMutation from './Schema/Mutations/EditPartnership.gql'
import ChargeDepartmentDebt from './Schema/Mutations/ChargeDepartmentDebt.gql'
import PaySetAmountForShipments from './Schema/Mutations/PaySetAmountForShipments.gql'
import FetchDepartments from './Schema/Queries/FetchDepartments.gql'
import FetchDepartment from './Schema/Queries/FetchDepartment.gql'
import FetchPlansQuery from './Schema/Queries/FetchPlans.gql'
import FetchDepartmentSuggestions from './Schema/Queries/FetchDepartmentSuggestions.gql'
import FetchPartnerships from './Schema/Queries/FetchPartnerships.gql'
import FetchDepartmentsMeta from './Schema/Queries/FetchDepartmentsMeta.gql'
import FetchDepartmentPaymentLogs from './Schema/Queries/FetchDepartmentPaymentLogs.gql'
import FetchDepartmentAddresses from './Schema/Queries/FetchDepartmentAddresses.gql'
import FetchCountries from './Schema/Queries/FetchCountries.gql'
import FetchCurrencies from './Schema/Queries/FetchCurrencies.gql'
import EditAddress from './Schema/Mutations/EditAddress.gql'
import CreateAddress from './Schema/Mutations/CreateAddress.gql'
import DeleteAddress from './Schema/Mutations/DeleteAddress.gql'
import FetchDepartmentVacation from './Schema/Queries/FetchDepartmentVacation.gql'
import FetchDepartmentVacations from './Schema/Queries/FetchDepartmentVacations.gql'
import CreateDepartmentVacation from './Schema/Mutations/CreateDepartmentVacation.gql'
import EditDepartmentVacation from './Schema/Mutations/EditDepartmentVacation.gql'
import DeleteDepartmentVacation from './Schema/Mutations/DeleteDepartmentVacation.gql'
import StartDepartmentVacationEarlier from './Schema/Mutations/StartDepartmentVacationEarlier.gql'
import EndDepartmentVacationEarlier from './Schema/Mutations/EndDepartmentVacationEarlier.gql'

export default {
	/**
     * ========================================================================
     * DEPARTMENT OPERATIONS (Single)
     * ========================================================================
     */

	/**
     * Fetch single department
     */
	async [ACTION_TYPES.FETCH_DEPARTMENT] ({ commit, rootState, state }, id) {
		if (state.department?.id !== id) {
			commit(MUTATIONS.SET_VACATION, null)
			commit(MUTATIONS.SET_LAST_VACATION, null)
		}
		const data = await executeSimpleQuery({
			query: FetchDepartment,
			variables: {
				id,
				isAdmin: !!rootState.Auth.user.admin
			}
		})

		const department = createDepartment(data.department)
		commit(MUTATIONS.SET_DEPARTMENT, department)
		return department
	},

	/**
     * Create department
     */
	async [ACTION_TYPES.CREATE_DEPARTMENT] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: CreateDepartmentMutation,
				variables: { payload }
			},
			onSuccess: (data) => data.createDepartment
		})
	},

	/**
     * Edit department
     */
	async [ACTION_TYPES.EDIT_DEPARTMENT] ({ commit, state }, payload) {
		return executeMutation({
			mutation: {
				mutation: EditDepartmentMutation,
				variables: { payload }
			},
			onSuccess: (data) => {
				const updatedDepartment = {
					...state.department,
					...data.editDepartment,
					...payload
				}
				commit(MUTATIONS.SET_DEPARTMENT, updatedDepartment)
				return data.editDepartment
			}
		})
	},

	/**
     * Delete department
     */
	async [ACTION_TYPES.DELETE_DEPARTMENT] ({ state, commit }, id) {
		// Optimistic update
		if (state.data) {
			const departments = state.data.filter(department => department.id !== id)
			commit(`Table/${TABLE_MUTATIONS.SET_DATA}`, departments)
			commit(MUTATIONS.SET_DEPARTMENTS, departments)
			commit(MUTATIONS.SET_META, {
				...state.meta,
				total: state.meta.total - 1
			})
		}

		return executeMutation({
			mutation: {
				mutation: DeleteDepartmentMutation,
				variables: { payload: { id } }
			},
			onSuccess: (data) => data.deleteDepartment
		})
	},

	/**
     * ========================================================================
     * DEPARTMENTS LISTING
     * ========================================================================
     */

	/**
     * Fetch departments
     */
	async [ACTION_TYPES.FETCH_DEPARTMENTS] (
		{ commit, state, _rootState },
		{ page = 1, size = 20, companyIds = [] } = {}
	) {
		const columns = buildDepartmentsColumns(state.Table.columns, state.Table.originalColumns)
		const sort = buildDepartmentsSort(state.Table)
		const payload = buildDepartmentsPayload(state.searchFilters, companyIds)

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'departments',
			query: {
				query: FetchDepartments,
				variables: {
					page,
					size,
					sort,
					payload,
					...columns
				}
			},
			onSuccess: (data) => {
				const departments = data.departments.nodes.map(dept =>
					createDepartment(dept)
				)
				commit(MUTATIONS.SET_DEPARTMENTS, departments)
				commit(`Table/${TABLE_MUTATIONS.SET_DATA}`, departments)
				return departments
			}
		})
	},

	/**
     * Fetch departments meta
     */
	async [ACTION_TYPES.FETCH_DEPARTMENTS_META] (
		{ commit, state },
		{ page, size, companyIds = [] }
	) {
		const payload = buildDepartmentsPayload(state.searchFilters, companyIds)

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'meta',
			query: {
				query: FetchDepartmentsMeta,
				variables: { page, size, payload }
			},
			onSuccess: (data) => {
				const meta = data.departments?.meta ?? state.meta
				commit(MUTATIONS.SET_META, meta)
				return meta
			}
		})
	},

	/**
     * Fetch departments search suggestions
     */
	async [ACTION_TYPES.FETCH_DEPARTMENTS_SEARCH_SUGGESTIONS] (
		{ rootState },
		{ searchPhrase, skipCompany = false }
	) {
		const payload = { searchPhrase }

		const companyId = rootState?.Auth?.user?.selectedCompanyId
		if (companyId && !skipCompany) {
			payload.companyIds = companyId
		}

		const data = await executeSimpleQuery({
			query: FetchDepartmentSuggestions,
			variables: {
				payload,
				size: 7
			}
		})
		return data.departmentSearchSuggestions.nodes
	},

	/**
     * ========================================================================
     * DEPARTMENT ADDRESSES
     * ========================================================================
     */

	/**
     * Fetch department addresses
     */
	async [ACTION_TYPES.FETCH_DEPARTMENT_ADDRESSES] ({ commit, state }, id) {
		const data = await executeSimpleQuery({
			query: FetchDepartmentAddresses,
			variables: { id }
		})

		const department = createDepartment({
			...state.department,
			...data.department
		})
		commit(MUTATIONS.SET_DEPARTMENT, department)
		return data.department
	},

	/**
     * Create address
     */
	async [ACTION_TYPES.CREATE_ADDRESS] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: CreateAddress,
				variables: { payload }
			},
			onSuccess: (data) => data.createAddress
		})
	},

	/**
     * Edit address
     */
	async [ACTION_TYPES.EDIT_ADDRESS] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: EditAddress,
				variables: { payload }
			},
			onSuccess: (data) => data.editAddress
		})
	},

	/**
     * Delete address
     */
	async [ACTION_TYPES.DELETE_ADDRESS] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: DeleteAddress,
				variables: { payload }
			},
			onSuccess: (data) => data.deleteAddress
		})
	},

	/**
     * ========================================================================
     * DEPARTMENT FINANCIAL OPERATIONS
     * ========================================================================
     */

	/**
     * Charge department debt
     */
	async [ACTION_TYPES.CHARGE_DEPARTMENT_DEBT] ({ state, commit }, departmentId) {
		return executeMutation({
			mutation: {
				mutation: ChargeDepartmentDebt,
				variables: { departmentId }
			},
			onSuccess: (data) => {
				const department = state.department
				commit(MUTATIONS.SET_DEPARTMENT, {
					...department,
					debt: 0,
					shipmentsEnabled: true
				})
				return data.chargeDepartmentDebt.status
			}
		})
	},

	/**
     * Pay amount for shipments
     */
	async [ACTION_TYPES.PAY_AMOUNT_FOR_SHIPMENTS] (_, payload) {
		return executeMutation({
			mutation: {
				mutation: PaySetAmountForShipments,
				variables: { payload }
			},
			onSuccess: (data) => data.paySetAmountForShipments
		})
	},

	/**
     * Fetch department payment logs
     */
	async [ACTION_TYPES.FETCH_DEPARTMENT_PAYMENT_LOGS] (_, departmentId) {
		const data = await executeSimpleQuery({
			query: FetchDepartmentPaymentLogs,
			fetchPolicy: 'no-cache',
			variables: {
				departmentId,
				size: 999
			}
		})
		return data.paymentLogs.nodes
	},

	/**
     * ========================================================================
     * VACATIONS
     * ========================================================================
     */

	/**
     * Fetch open (not done / cancelled) department vacation.
     * Pass commit: false to read it without touching department page state.
     */
	async [ACTION_TYPES.FETCH_DEPARTMENT_VACATION] ({ commit }, { departmentId, commit: shouldCommit = true }) {
		const data = await executeSimpleQuery({
			query: FetchDepartmentVacation,
			fetchPolicy: 'no-cache',
			variables: { departmentId }
		})
		const vacation = data.departmentVacation
			? createDepartmentVacation(data.departmentVacation)
			: null
		if (shouldCommit) {
			commit(MUTATIONS.SET_VACATION, vacation)
		}
		return vacation
	},

	/**
     * Fetch the latest finished department vacation (for the return summary)
     */
	async [ACTION_TYPES.FETCH_LAST_DEPARTMENT_VACATION] ({ commit }, departmentId) {
		const data = await executeSimpleQuery({
			query: FetchDepartmentVacations,
			fetchPolicy: 'no-cache',
			variables: {
				payload: { departmentIds: [departmentId], statuses: ['done'] },
				page: 1,
				size: 1,
				sort: { column: 'id', order: 'desc' }
			}
		})
		const node = data.departmentVacations?.nodes?.[0]
		const vacation = node ? createDepartmentVacation(node) : null
		commit(MUTATIONS.SET_LAST_VACATION, vacation)
		return vacation
	},

	async [ACTION_TYPES.CREATE_DEPARTMENT_VACATION] ({ commit }, payload) {
		return executeMutation({
			mutation: {
				mutation: CreateDepartmentVacation,
				variables: { payload }
			},
			onSuccess: (data) => {
				const vacation = createDepartmentVacation(data.createDepartmentVacation)
				commit(MUTATIONS.SET_VACATION, vacation)
				return vacation
			}
		})
	},

	async [ACTION_TYPES.EDIT_DEPARTMENT_VACATION] ({ commit }, payload) {
		return executeMutation({
			mutation: {
				mutation: EditDepartmentVacation,
				variables: { payload }
			},
			onSuccess: (data) => {
				const vacation = createDepartmentVacation(data.editDepartmentVacation)
				commit(MUTATIONS.SET_VACATION, vacation)
				return vacation
			}
		})
	},

	async [ACTION_TYPES.DELETE_DEPARTMENT_VACATION] ({ commit }, id) {
		return executeMutation({
			mutation: {
				mutation: DeleteDepartmentVacation,
				variables: { payload: { id } }
			},
			onSuccess: (data) => {
				commit(MUTATIONS.SET_VACATION, null)
				return data.deleteDepartmentVacation
			}
		})
	},

	async [ACTION_TYPES.START_EARLY_DEPARTMENT_VACATION] ({ commit }, id) {
		return executeMutation({
			mutation: {
				mutation: StartDepartmentVacationEarlier,
				variables: { payload: { id } }
			},
			onSuccess: (data) => {
				const vacation = createDepartmentVacation(data.startDepartmentVacationEarlier)
				commit(MUTATIONS.SET_VACATION, vacation)
				return vacation
			}
		})
	},

	async [ACTION_TYPES.END_EARLY_DEPARTMENT_VACATION] ({ commit }, id) {
		return executeMutation({
			mutation: {
				mutation: EndDepartmentVacationEarlier,
				variables: { payload: { id } }
			},
			onSuccess: (data) => {
				const vacation = createDepartmentVacation(data.endDepartmentVacationEarlier)
				commit(MUTATIONS.SET_VACATION, vacation)
				return vacation
			}
		})
	},

	/**
     * ========================================================================
     * PARTNERSHIPS
     * ========================================================================
     */

	/**
     * Fetch partnerships
     */
	async [ACTION_TYPES.FETCH_PARTNERSHIPS] ({ rootState, commit }, departmentIds) {
		const payload = {}

		if (!departmentIds) {
			const selectedDepartmentId = rootState?.Auth?.user?.selectedDepartmentId
			payload.departmentIds = selectedDepartmentId || []
		} else {
			payload.departmentIds = departmentIds
		}

		return executeQueryWithLoading({
			commit,
			mutationName: MUTATIONS.SET_LOADING,
			loadingKey: 'partnerships',
			query: {
				query: FetchPartnerships,
				variables: {
					payload,
					size: 999,
					page: 1
				}
			},
			onSuccess: (data) => {
				const partnerships = data.partnerships.nodes
				commit(MUTATIONS.SET_PARTNERSHIPS, partnerships)
				return partnerships
			}
		})
	},

	/**
     * Create partnership
     */
	async [ACTION_TYPES.CREATE_PARTNERSHIP] ({ state, commit }, payload) {
		return executeMutation({
			mutation: {
				mutation: CreatePartnershipMutation,
				variables: { payload }
			},
			onSuccess: (data) => {
				const partnership = data.createPartnership
				const updatedPartnerships = [...state.partnerships, partnership]
				commit(MUTATIONS.SET_PARTNERSHIPS, updatedPartnerships)
				return updatedPartnerships
			}
		})
	},

	/**
     * Edit partnership
     */
	async [ACTION_TYPES.EDIT_PARTNERSHIP] ({ state, commit }, payload) {
		return executeMutation({
			mutation: {
				mutation: EditPartnershipMutation,
				variables: { payload }
			},
			onSuccess: (data) => {
				const partnership = data.editPartnership
				const updatedPartnerships = updatePartnershipInList(
					state.partnerships,
					partnership
				)
				commit(MUTATIONS.SET_PARTNERSHIPS, updatedPartnerships)
				return partnership
			}
		})
	},

	/**
     * ========================================================================
     * PLANS & COUNTRIES
     * ========================================================================
     */

	/**
     * Fetch plans
     */
	async [ACTION_TYPES.FETCH_PLANS] ({ commit }) {
		const data = await executeSimpleQuery({
			query: FetchPlansQuery
		})

		const plans = data.plans.nodes.map(plan => createPlan(plan))
		commit(MUTATIONS.SET_PLANS, plans)
		return plans
	},

	/**
     * Fetch countries
     */
	async [ACTION_TYPES.FETCH_COUNTRIES] ({ commit }) {
		const data = await executeSimpleQuery({
			query: FetchCountries
		})

		const countries = data?.countries?.nodes?.map(country => ({
			id: country.code,
			name: country.name
		})) ?? []

		commit(MUTATIONS.SET_COUNTRIES, countries)
		return countries
	},

	async [ACTION_TYPES.FETCH_CURRENCIES] ({ commit }) {
		const data = await executeSimpleQuery({
			query: FetchCurrencies
		})

		commit(MUTATIONS.SET_CURRENCIES, data?.currencies)
		return data?.currencies
	}
}
